import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { fetchManagerialDashboard } from '@/lib/api/managerial-dashboard-admin.service'
import type {
  ManagerialDashboard,
  ManagerialDashboardAudience,
  ManagerialDashboardSection,
} from '@/types/api'
import {
  parseExecutiveResourceId,
  type ExecutiveFiltersState,
} from '../lib/executive-filters'
import {
  getSalesEvolutionChartNotice,
  type SalesEvolutionChartQuery,
} from '../lib/sales-evolution'
import { DashboardLoader } from '../lib/dashboard-loader'
import { DASHBOARD_SECTIONS } from '../lib/dashboard-sections'

export function useManagerialDashboard(
  filters: ExecutiveFiltersState,
  chart: SalesEvolutionChartQuery,
  audience: ManagerialDashboardAudience = 'admin'
) {
  const [loader] = useState(() => new DashboardLoader(fetchManagerialDashboard))
  const snapshot = useSyncExternalStore(loader.subscribe, loader.getSnapshot)
  const chartNotice = getSalesEvolutionChartNotice(chart)
  const chartPeriod = chartNotice === null ? chart.period : '30d'
  const chartFrom = chartPeriod === 'custom' ? chart.customFrom : null
  const chartTo = chartPeriod === 'custom' ? chart.customTo : null
  const institutionId = parseExecutiveResourceId(filters.institution)
  const campaignId = parseExecutiveResourceId(filters.campaign)

  useEffect(() => {
    loader.activate()
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') loader.refresh(undefined, false)
    }
    document.addEventListener('visibilitychange', refreshVisible)
    window.addEventListener('focus', refreshVisible)
    return () => {
      loader.dispose()
      document.removeEventListener('visibilitychange', refreshVisible)
      window.removeEventListener('focus', refreshVisible)
    }
  }, [loader])

  useEffect(() => {
    loader.configure({
      period: filters.period,
      status: filters.status,
      institutionId,
      campaignId,
      chartPeriod,
      chartFrom,
      chartTo,
      audience,
    })
  }, [
    loader,
    filters.period,
    filters.status,
    institutionId,
    campaignId,
    chartPeriod,
    chartFrom,
    chartTo,
    audience,
  ])

  const sections =
    loader.getAudience() === audience ? snapshot : loader.getEmptySnapshot()
  const data = useMemo(() => {
    let combined: Partial<ManagerialDashboard> = {}
    let received = false
    for (const section of DASHBOARD_SECTIONS) {
      const block = sections[section].data
      if (!block) continue
      received = true
      const { section: _section, measurements, ...fields } = block
      void _section
      combined = { ...combined, ...fields }
      if (measurements) {
        const groups = [
          ...(combined.measurements?.sections ?? []),
          ...measurements.sections,
        ]
        const order = ['relationship', 'buyers', 'efficiency', 'funnel', 'products']
        combined.measurements = {
          ...measurements,
          sections: groups.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id)),
        }
      }
    }
    if (sections.overview.data)
      combined.generated_at = sections.overview.data.generated_at
    return received ? combined : null
  }, [sections])
  const failures = DASHBOARD_SECTIONS.filter(
    (section) => sections[section].error !== null
  )
  const error =
    failures.length === 0
      ? null
      : failures.length === DASHBOARD_SECTIONS.length && !data
        ? sections.overview.error
        : `${failures.length} bloco(s) não puderam ser atualizados. Os demais continuam disponíveis.`
  return {
    data,
    filterOptions:
      loader.getAudience() === audience ? loader.getFilterOptions() : undefined,
    sections,
    error,
    chartNotice,
    isLoading: DASHBOARD_SECTIONS.some((section) => sections[section].isLoading),
    loadedCount: DASHBOARD_SECTIONS.filter((section) => sections[section].data !== null)
      .length,
    sectionCount: DASHBOARD_SECTIONS.length,
    refresh: () => loader.refresh(),
    retry: (section: ManagerialDashboardSection) => loader.refresh(section),
  }
}
